import axios from 'axios';
import authService from './auth';

const hostname = window && window.location && window.location.hostname;

let backendHost;
if(hostname === 'rentreceipt.app') {
  backendHost = 'https://rentreceipt.app';
} else {
  backendHost = 'http://localhost:8200';
}

// Create axios instance with base URL
const api = axios.create({
  baseURL: `${backendHost}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Property API methods
export const propertyApi = {
  getAll: () => api.get('/properties'),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Tenant API methods
export const tenantApi = {
  getAll: () => api.get('/tenants'),
  getById: (id) => api.get(`/tenants/${id}`),
  getByProperty: (propertyId) => api.get(`/tenants/property/${propertyId}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
  getEmailTemplate: (id) => api.get(`/tenants/${id}/email-template`),
  updateEmailTemplate: (id, data) => api.put(`/tenants/${id}/email-template`, data),
};

// Receipt API methods
export const receiptApi = {
  getAll: () => api.get('/receipts'),
  getById: (id) => api.get(`/receipts/${id}`),
  getByTenant: (tenantId) => api.get(`/receipts/tenant/${tenantId}`),
  getYearlySummary: (tenantId) => api.get(`/receipts/tenant/${tenantId}/yearly-summary`),
  getProblems: () => api.get('/receipts/problems'),
  import: (formData) => api.post('/receipts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateSentStatus: (id, data) => api.patch(`/receipts/${id}/sent-status`, data),
  generate: (data) => api.post('/receipts', data),
  sendEmail: (id, toEmail) => api.post(`/receipts/${id}/send-email`, { toEmail }),
  delete: (id, force = false) => api.delete(`/receipts/${id}${force ? '?force=true' : ''}`),
  download: (id) => {
    const token = authService.getToken();

    // Create a download link with authorization header
    const downloadUrl = `${api.defaults.baseURL}/receipts/download/${id}`;

    // Create a hidden anchor element
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', '');
    link.setAttribute('target', '_blank');

    // Create a separate fetch request with the auth token
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
    .catch(error => {
      console.error('Download error:', error);
    });

    return Promise.resolve();
  },
};

// Contract API methods
export const contractApi = {
  getAll: () => api.get('/contracts'),
  getById: (id) => api.get(`/contracts/${id}`),
  getByProperty: (propertyId) => api.get(`/contracts/property/${propertyId}`),
  getByTenant: (tenantId) => api.get(`/contracts/tenant/${tenantId}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  sign: (id, data) => api.post(`/contracts/${id}/sign`, data),
  activate: (id) => api.post(`/contracts/${id}/activate`),
  generatePDF: (id) => api.post(`/contracts/${id}/generate-pdf`),
  sendEmail: (id, toEmail) => api.post(`/contracts/${id}/send-email`, { toEmail }),
  download: (id) => {
    const token = authService.getToken();

    // Create a download link with authorization header
    const downloadUrl = `${api.defaults.baseURL}/contracts/download/${id}`;

    // Create a separate fetch request with the auth token
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${id}.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
    .catch(error => {
      console.error('Download error:', error);
    });

    return Promise.resolve();
  },
};

// État des lieux API methods
export const etatDesLieuxApi = {
  getAll: () => api.get('/etat-des-lieux'),
  getById: (id) => api.get(`/etat-des-lieux/${id}`),
  getByProperty: (propertyId) => api.get(`/etat-des-lieux/property/${propertyId}`),
  getByTenant: (tenantId) => api.get(`/etat-des-lieux/tenant/${tenantId}`),
  create: (data) => api.post('/etat-des-lieux', data),
  update: (id, data) => api.put(`/etat-des-lieux/${id}`, data),
  delete: (id) => api.delete(`/etat-des-lieux/${id}`),
  sign: (id, data) => api.post(`/etat-des-lieux/${id}/sign`, data),
  generatePDF: (id) => api.post(`/etat-des-lieux/${id}/generate-pdf`),
  download: (id) => {
    const token = authService.getToken();

    // Create a download link with authorization header
    const downloadUrl = `${api.defaults.baseURL}/etat-des-lieux/${id}/download`;

    // Create a separate fetch request with the auth token
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etat_des_lieux_${id}.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
    .catch(error => {
      console.error('Download error:', error);
    });

    return Promise.resolve();
  },
};

// Blog API methods
export const blogApi = {
  getAll: (params) => api.get('/blog', { params }), // Public endpoint
  getById: (id) => api.get(`/blog/${id}`), // Public endpoint
  getAllAdmin: (params) => api.get('/blog/all', { params }), // Admin only
  create: (data) => api.post('/blog', data), // Admin only
  update: (id, data) => api.put(`/blog/${id}`, data), // Admin only
  delete: (id) => api.delete(`/blog/${id}`), // Admin only
};

// Admin API methods
export const adminApi = {
  getAllUsers: () => api.get('/admin/users'), // Admin only
  getStats: () => api.get('/admin/users/stats'), // Admin only
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }), // Admin only
  deleteUser: (id) => api.delete(`/admin/users/${id}`), // Admin only
};

// Handle global errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response || error);
    return Promise.reject(error);
  }
);

export default api;